import type { ShellFramePlan, ShellInjectionRef } from './kernel-contract.js';
import type { AppMeta, OrgMetadata, SessionIdentity } from './metadata-source.js';
import type { ShellRuntime } from './surfaces/shell-frame.js';

/**
 * CAOS-267 — turn the kernel's `hand-to-shell` boot decision into the REAL post-install workspace.
 *
 * The kernel decision names the mastered shell + the packages injecting into its seams, and the
 * metadata retrieve names the org's installed apps + tabs (see `metadata-source.ts`). This module
 * joins the two:
 *   • {@link enrichShellPlan} projects the installed apps into the `navigation-entries` seam (with
 *     real labels, icons, and each app's object tabs), and resolves a real `label` onto every other
 *     seam's injections — so the App Launcher lists the actual apps and an edge/avatar button reads
 *     its real name instead of a humanized package key.
 *   • {@link buildShellRuntime} reads the browser route to pick the active app + tab, so opening
 *     `/sales` shows Sales Atlas and its tabs, and wires navigation so the launcher/nav are live.
 *
 * Pure (no globals, no I/O) so it is unit-tested directly; production feeds it the fetched metadata
 * and the real `window.location` + a navigate callback.
 */

/** The seam the installed apps populate — the App Launcher's list and the app-scoped nav strip. */
const NAVIGATION_ENTRIES = 'navigation-entries';

/**
 * CAOS-507 — the environment identity shown beside every destination. The org registration kind is
 * server-resolved by whoami (CAOS-1008): paid is production, while sandbox/trial are named as such.
 * An unregistered org stays unlabeled rather than being guessed from its hostname or display name.
 */
export function environmentLabelFor(
  identity: Pick<SessionIdentity, 'orgLabel' | 'orgType'>,
): string | undefined {
  const kind =
    identity.orgType === 'paid'
      ? 'PRODUCTION'
      : identity.orgType === 'sandbox'
        ? 'SANDBOX'
        : identity.orgType === 'trial'
          ? 'TRIAL'
          : undefined;
  if (!kind) return undefined;
  const org = identity.orgLabel?.trim();
  return org ? `${kind} · ${org}` : kind;
}

/**
 * Return a copy of the boot plan with the org's real installed apps + resolved injection labels.
 * The apps REPLACE whatever the kernel projected into `navigation-entries` (the kernel does not yet
 * enumerate apps there); every other seam is preserved, with a `label` added from the metadata's
 * source-key → label map when one is known (unknown ⇒ left for the frame to humanize).
 */
export function enrichShellPlan(plan: ShellFramePlan, meta: OrgMetadata): ShellFramePlan {
  const injections: Record<string, ShellInjectionRef[]> = {};

  // Carry every kernel seam EXCEPT navigation-entries (apps own that), resolving labels. CAOS-320 —
  // an edge-anchored injection whose source package contributes an intake form gets that form attached,
  // so the orb opens a real working surface instead of a static button.
  for (const [seam, refs] of Object.entries(plan.injections)) {
    if (seam === NAVIGATION_ENTRIES) continue;
    injections[seam] = refs.map((ref) => {
      const label = ref.label ?? meta.labels.get(ref.sourceKey);
      const intake = meta.intakeBySource.get(ref.sourceKey);
      // CAOS-148 — and one whose source package roots guidance recipes gets those, with the anchors
      // they name, so its help tab opens the package's own guidance instead of the placeholder.
      const guidance = meta.guidanceBySource.get(ref.sourceKey);
      // CAOS-688 — and one whose source package declares an assistant panel gets that panel, so its
      // assistant tab opens the package's own peek and jump instead of a chat box with the host's
      // words in it.
      const assistant = meta.assistantBySource.get(ref.sourceKey);
      // CAOS-151 — and the panel kind that package's own claim declares. The kernel plan carries an
      // edge tab's placement but not its kind, so without this every installed edge tab would be
      // classified by its key and label text. A kind the plan already carries stands.
      const kind =
        seam === 'edge-anchored' && ref.anchor && !ref.anchor.kind ? meta.anchorKindBySource?.get(ref.sourceKey) : undefined;
      const anchor = kind ? { ...ref.anchor!, kind } : ref.anchor;
      return {
        ...ref,
        ...(anchor ? { anchor } : {}),
        ...(label ? { label } : {}),
        ...(intake ? { intake } : {}),
        ...(guidance ? { guidance } : {}),
        ...(assistant ? { assistant } : {}),
      };
    });
  }

  // CAOS-756 — this used to unconditionally fabricate a "Guidance Center" edge tab here, on EVERY
  // workspace render, regardless of whether `pkg_guidance` was actually installed on the org: a bare
  // Core-only org (no Guidance package installed) still showed the tab, wired to hardcoded
  // host-baked content. That was the packaging defect CAOS-756 closed. The edge seam now carries
  // ONLY what the kernel plan actually contains — the real installed packages' own claims — with
  // labels resolved from the org's real metadata below, same as any other seam.
  // The installed apps ARE the navigation entries: one per app, carrying its tabs.
  if (meta.apps.length > 0) {
    injections[NAVIGATION_ENTRIES] = meta.apps.map((app) => ({
      injectionPoint: NAVIGATION_ENTRIES,
      sourceKey: app.key,
      label: app.label,
      icon: app.icon,
      tabs: app.tabs,
      landingTab: app.landingTab,
      settingsPage: app.settingsPage,
      overflowAfter: app.overflowAfter,
      showInAppSwitcher: app.showInAppSwitcher,
    }));
  }

  return { ...plan, injections };
}

/**
 * CAOS-606 — the plan as it looks while previewing the workspace as another role.
 *
 * `visibleObjects` is the set of object keys the previewed role can reach, as the kernel resolved
 * it. A tab bound to an object outside that set is dropped, and an app left with no tabs at all is
 * dropped with them — an app whose every tab has gone is a launcher entry that opens onto nothing.
 *
 * ── WHAT IS DELIBERATELY NOT DROPPED ─────────────────────────────────────────────────────────
 * A tab that points at a PAGE or an external address rather than an object. There is no object
 * permission to resolve one against — a page's own reachability is a question this platform does
 * not answer yet — and guessing would hide a working tab on no evidence. The same goes for an app
 * that declares no object tabs at all: it is left exactly as it is.
 *
 * ── AND WHY THIS ONLY EVER RUNS UNDER A PREVIEW ──────────────────────────────────────────────
 * Nothing filters the workspace's apps or tabs by permission today: `metadata retrieve` returns
 * the org's whole component set and the shell draws all of it for everybody, with access enforced
 * when data is read rather than when a tab is drawn. Whether that is right is a real question and
 * a separate one — answering it here would change what every person in every org sees, on a ticket
 * about a switcher. So this is reached only when a preview is in force: with none, the plan is
 * returned unchanged, by identity, and nobody who is not previewing sees any difference.
 */
export function narrowPlanToPreview(plan: ShellFramePlan, visibleObjects: readonly string[] | null): ShellFramePlan {
  if (!visibleObjects) return plan;
  const visible = new Set(visibleObjects);
  const apps = plan.injections[NAVIGATION_ENTRIES];
  if (!apps || apps.length === 0) return plan;

  const narrowed: ShellInjectionRef[] = [];
  for (const app of apps) {
    const tabs = app.tabs;
    if (!tabs || tabs.length === 0) {
      narrowed.push(app);
      continue;
    }
    const kept = tabs.filter((t) => t.object === undefined || visible.has(t.object));
    if (kept.length === 0) continue; // every tab gone ⇒ the app opens onto nothing
    if (kept.length === tabs.length) {
      narrowed.push(app);
      continue;
    }
    // The landing tab may be one of the ones that went. Drop the declaration rather than keep a
    // pointer at a tab this plan no longer has; the frame already resolves a landing from the
    // remaining tab set when none is declared.
    const landingSurvives = app.landingTab !== undefined && kept.some((t) => t.key === app.landingTab);
    const { landingTab: _dropped, ...rest } = app;
    narrowed.push({ ...rest, tabs: kept, ...(landingSurvives ? { landingTab: app.landingTab! } : {}) });
  }

  return { ...plan, injections: { ...plan.injections, [NAVIGATION_ENTRIES]: narrowed } };
}

/** The seam an assistant panel is anchored in, and the permission the platform gates AI on. */
const EDGE_ANCHORED = 'edge-anchored';
const AI_USE = 'ai.use';

/**
 * CAOS-157 — drop an assistant entry point this person cannot use.
 *
 * ── THE DEFECT ───────────────────────────────────────────────────────────────────────────────
 * `injectionsFor` (kernel `capabilities/registry.ts`) selects a seam's contributions by ORG, and
 * {@link enrichShellPlan} carries every one of them through. Neither asks who is looking. That is
 * right for most seams — an app in the launcher is an app the org installed — but the assistant
 * anchor is different, because `ai.use` already gates every shipped AI capability (CAOS-181). So a
 * person without it saw the panel, opened it, and was refused: an entry point to nothing, which
 * reads as a broken assistant rather than as one they were never granted.
 *
 * ── WHY THIS IS NOT A NEW PRODUCT RULE ───────────────────────────────────────────────────────
 * The platform already decided `ai.use` is the gate. All this does is stop advertising a door
 * behind it. `anchor.kind` is already in the claim schema (`assistant | report | help`), so the
 * rule is expressible against metadata that exists; no claim needs a new field, and no other kind
 * of anchor is touched — a `report` or `help` tab has nothing to do with `ai.use`.
 *
 * ── IT IS NOT AN ACCESS CHECK, AND MUST NOT BE READ AS ONE ───────────────────────────────────
 * This decides what to SHOW. `heldPermissions` is reported by whoami for exactly that purpose and
 * says so in terms: "Every route still decides for itself what this caller may reach." A client
 * that lied to itself here would gain nothing — the capability's own `requires` is checked
 * server-side against the caller on its own handle.
 *
 * ── ABSENT MEANS UNKNOWN, AND UNKNOWN KEEPS THE TAB ──────────────────────────────────────────
 * `heldPermissions` is optional on {@link SessionIdentity}: an older server, or a read that could
 * not answer, supplies nothing. Treating absence as "holds nothing" would hide the assistant from
 * everybody the moment that read failed — a silent, total feature outage from a degraded identity
 * call. An EMPTY array is a different thing: the server answered and this person holds none, so the
 * tab goes. The two cases are distinguished rather than collapsed, which is the whole reason this
 * takes the identity and not a `readonly string[]`.
 *
 * ── AND WHY THE REAL HOLDING RATHER THAN THE PREVIEWED ROLE ──────────────────────────────────
 * Deliberately not following a "Viewing as" preview, matching what `main.ts` already decided for
 * `heldPermissions` and for the same reason: CAOS-606 narrows the object and field planes only, and
 * the AI routes resolve against the REAL principal. An anchor shown because the previewed role
 * would hold `ai.use` would open a panel that then refused, and the banner above it would appear to
 * vouch for the fiction.
 *
 * ── ORDER AGAINST `narrowPlanToPreview` IS CURRENTLY IRRELEVANT, AND THAT IS INCIDENTAL ──────
 * `main.ts` applies the preview narrowing first and this second, but either order gives the same
 * answer today because the two touch DISJOINT SEAMS: {@link narrowPlanToPreview} only ever rewrites
 * `navigation-entries`, and this only ever rewrites `edge-anchored`. Nothing enforces that, so it
 * is worth saying rather than relying on: whichever of the two grows into the other's seam has to
 * decide the composition on purpose, and the first symptom of not deciding would be a contribution
 * that survives one narrowing by being removed by the other first.
 */
export function narrowPlanToHeldPermissions(
  plan: ShellFramePlan,
  identity: Pick<SessionIdentity, 'heldPermissions'>,
): ShellFramePlan {
  const held = identity.heldPermissions;
  // Unknown — keep everything. See the docblock: this is not the same answer as an empty array.
  if (!held) return plan;
  const edge = plan.injections[EDGE_ANCHORED];
  if (!edge || edge.length === 0) return plan;
  if (held.includes(AI_USE)) return plan;

  const kept = edge.filter((ref) => ref.anchor?.kind !== 'assistant');
  if (kept.length === edge.length) return plan;
  return { ...plan, injections: { ...plan.injections, [EDGE_ANCHORED]: kept } };
}

/** The active app + tab a route resolves to, against the plan's installed apps. */
export interface ActiveRoute {
  readonly appKey?: string;
  readonly tabKey?: string;
}

/**
 * CAOS-1651 — the app this org designates as its HOME, resolved against what is installed, or
 * `undefined` when the org's home is the platform's own Home page.
 *
 * This is the one resolution of the org's home, and everything that speaks about home reads it:
 * the root path on the initial paint, the root path on an in-place navigation, and — the half this
 * ticket added — what the leading anchor is CALLED. The anchor's text and its destination agreeing
 * is the acceptance criterion that mattered, and sharing the resolution is what makes it structural
 * rather than a rule somebody has to keep: there is no second value to drift.
 *
 * It returns the ENTRY rather than a key and a label, so the name still comes from `labelOf` in
 * `shell-frame.ts` — the single place an injection's label is resolved (CAOS-1519), which guards
 * against a non-string arriving through the unvalidated boot envelope. Resolving the name a second
 * time here would be a second place for that to go wrong.
 *
 * `undefined` covers three different orgs on purpose, because the platform Home is the right answer
 * to all three: one that designates nothing, one that designates an app it has since uninstalled
 * (`find` misses rather than selecting something absent), and one that designates the shell MASTER
 * — `workspace` is not in `navigation-entries` at all, which is why a starter org, which designates
 * exactly that, is unchanged by any of this.
 */
export function resolveOrgHomeApp(plan: ShellFramePlan): ShellInjectionRef | undefined {
  if (!plan.defaultApp) return undefined;
  const apps = plan.injections[NAVIGATION_ENTRIES] ?? [];
  return apps.find((a) => a.sourceKey === plan.defaultApp);
}

/**
 * Resolve the active app + tab from a URL path against the enriched plan. `/sales/accounts` →
 * `{ app: sales, tab: accounts }`; `/sales` → `{ app: sales }` (its landing tab is resolved by the
 * frame). An unknown app segment resolves to Home rather than fabricating a selection, so a stale
 * link lands on the home instead of silently opening some other app.
 *
 * THE ROOT PATH `/` ASKS THE ORG WHERE IT LANDS (CAOS-291). It resolves `plan.defaultApp` — the
 * org's own `org.defaultApp` designation — against the installed apps, and falls back to `{}`, the
 * **Atlantis AI Home** with NO app pre-selected, mirroring the original Dragon OS.
 *
 * The fallback is doing most of the work, and it is why this change cannot alter what a fresh org
 * sees. A starter org designates `workspace`, and `workspace` is the shell MASTER rather than an
 * injected app — it is not in `navigation-entries` at all — so it does not match, and root lands on
 * the home exactly as it did before.
 *
 * That last point is an INVARIANT NOTHING ENFORCES, so it is worth naming. A component reaches this
 * seam only by declaring `{capability:'shell', claim:'inject', injectionPoint:'navigation-entries'}`,
 * and `pkg_core_shell` declares one claim, a master claim — but no code refuses a package that
 * declared such a claim on a component keyed `workspace`, and if one ever did, every starter org
 * would start landing there instead. A kernel test reads the SHIPPED registry and asserts the core
 * shell injects nothing, so that change would be noticed rather than discovered. An org that designates nothing lands on the home too. An org
 * that designates an app it has since uninstalled lands on the home rather than on a selection
 * pointing at something absent. Only an org that designates an app it actually has gets something
 * different, which is the whole of what was missing.
 *
 * What this replaces is a HARDCODED absence. The screen is the same; the reason is no longer a
 * constant. `org.defaultApp` was declared, reference-validated, protected from deletion and written
 * at provisioning, and read by nothing — a designation an administrator could set and no code would
 * consult.
 */
export function resolveActiveRoute(pathname: string, plan: ShellFramePlan): ActiveRoute {
  const apps = plan.injections[NAVIGATION_ENTRIES] ?? [];
  if (apps.length === 0) return {};

  const segments = pathname.split('/').filter(Boolean);
  const routeApp = segments[0];
  if (!routeApp) {
    // Root ⇒ the org's designated landing app, else the Atlantis AI Home with no app selected.
    const designated = resolveOrgHomeApp(plan);
    return designated ? { appKey: designated.sourceKey } : {};
  }

  const active = apps.find((a) => a.sourceKey === routeApp);
  if (!active) return {}; // unknown app ⇒ Home, never a fabricated selection

  const routeTab = segments[1];
  const tab = (active.tabs ?? []).find((t) => t.key === routeTab);
  return { appKey: active.sourceKey, tabKey: tab?.key };
}

/**
 * CAOS-694/CAOS-342 — where the account menu's per-app "Settings" entry should go for a route, or
 * `undefined` when it must not be offered on that route at all.
 *
 * Real only when BOTH halves hold: the app the ROUTE selects declares a `settingsPage`, AND this
 * person administers that app. Neither alone is enough — an app with no settings surface has
 * nowhere to send the click, and a non-administering person must not be offered a control that
 * opens an app's configuration. An absent app key (the Atlantis AI Home, nothing selected) never
 * resolves an href either: there is no active app to administer.
 *
 * CAOS-1659 — PER ROUTE, and that is the fix rather than a refactor. Both halves were evaluated
 * once, against the pathname the frame was assembled on, and the frame keeps its top chrome
 * mounted across an in-place navigation. So after moving between apps the entry was still offered
 * and still pointed at the app you LEFT: standing in Service, choosing Settings opened Sales'.
 * The second half is the worse half — the entry could be offered while standing in an app this
 * person does not administer, which is the condition the gate exists to prevent.
 *
 * Pure, exported and taking its route as an argument so the two halves can be proven against a
 * SEQUENCE of routes rather than one. Resolving it inside `buildShellRuntime` and handing over a
 * string cannot be tested for this class of defect at all: every test that builds a runtime
 * happens to read it on the route it built, which is the one route the old code got right.
 */
export function resolveSettingsHref(
  plan: ShellFramePlan,
  administers: readonly string[] | undefined,
  route: string,
): string | undefined {
  // The app comes from the PATH; a query or fragment never selects one. The frame's navigator
  // strips both before it asks, but this is the gate's own guarantee rather than its caller's.
  const pathname = route.split('?')[0]!.split('#')[0]!;
  const { appKey } = resolveActiveRoute(pathname, plan);
  if (appKey === undefined) return undefined;
  if (!(administers?.includes(appKey) ?? false)) return undefined;
  const apps = plan.injections[NAVIGATION_ENTRIES] ?? [];
  if (!apps.find((a) => a.sourceKey === appKey)?.settingsPage) return undefined;
  return `/${appKey}/settings`;
}

/** What {@link buildShellRuntime} needs from the environment (kept injectable for tests). */
export interface ShellRuntimeInput {
  /** The current location path, e.g. `location.pathname`. */
  readonly pathname: string;
  /** A display name for the signed-in user (e.g. the sign-in email); absent ⇒ the block degrades. */
  readonly userName?: string;
  /**
   * CAOS-342 — the second line of the account block: the active role and the workspace, the way the
   * reference reads "Admin · Contoso". Absent ⇒ only the name shows.
   */
  readonly userSub?: string;
  /** CAOS-342 — the roles the signed-in person holds; they fill the "Viewing as" preview. */
  readonly userRoles?: readonly { readonly key: string; readonly label: string }[];
  /**
   * CAOS-694/CAOS-342 — the app keys this person ADMINISTERS (`SessionIdentity.administers`).
   * Drives the account menu's "Settings" entry: it appears only when the ACTIVE app's key is a
   * member of this list AND the active app declares a `settingsPage`. Absent/empty ⇒ the entry
   * never appears, on any app.
   */
  readonly administers?: readonly string[];
  /**
   * CAOS-507 — the authoritative environment label shown in persistent chrome and the document
   * title. The caller derives it from the server-resolved org type; this module never guesses.
   */
  readonly environmentLabel?: string;
  /**
   * CAOS-607 — present only while this session is acting as somebody else. The two labels are
   * what the banner names, and the banner is drawn from runtime state rather than from the plan
   * because who you are acting as is a property of the SESSION, not of the workspace's shape.
   */
  readonly impersonation?: {
    readonly targetLabel: string;
    readonly realLabel?: string;
    /** When the borrowed session runs out (ISO) — the strip's countdown. */
    readonly expiresAt?: string;
  };
  /**
   * CAOS-607 — the people this session may act as, as the kernel named them. Absent/empty ⇒ the
   * account menu's "Log in as another user" entry does not appear. Resolved once per load, for the
   * same reason as `impersonation` above: it is a fact about the session, not about the shape of
   * the workspace, and the frame is re-assembled on every navigation.
   */
  readonly impersonationOptions?: readonly { readonly id: string; readonly label: string }[];
  /**
   * CAOS-606 — the roles this person may preview the workspace as, and the one in force.
   *
   * These fill the account menu's "Viewing as" switcher and the banner over it. They are separate
   * from `userRoles` above on purpose: that list feeds the account block's second line and reports
   * only DIRECTLY assigned sets, while this one comes from the kernel's own preview resolver and
   * includes a set held through a permission set group. Offering a narrower list than the resolver
   * honours would mean the menu and the workspace disagreed about which roles somebody holds.
   */
  readonly previewRoles?: readonly { readonly key: string; readonly label: string }[];
  /** The role being previewed as, or absent when the workspace is resolving normally. */
  readonly activePreview?: { readonly key: string; readonly label: string } | null;
  /**
   * CAOS-608 — asks where "Edit this page" should go for a route, or `undefined` when that route
   * has nothing editable behind it.
   *
   * Passed IN rather than resolved here, unlike `settingsHrefFor`, and the reason is structural: the
   * gate depends on whether the content region is showing a RECORD, which only
   * `parseContentRoute` answers, and `content-source.ts` already imports `landingTabKey` from this
   * module. Resolving it here would make that import circular. So it is resolved beside the parser
   * that knows the answer (`resolveEditPageHref`), and this module passes it through.
   *
   * A function rather than a string because the frame re-asks it on every in-place navigation —
   * see `editPageHrefFor` on `ShellRuntime` for why a value resolved once is wrong.
   */
  readonly editPageHrefFor?: (route: string) => string | undefined;
  /** Navigation callback — production passes `(href) => window.location.assign(href)`. */
  readonly navigate: (href: string) => void;
}

/**
 * Build the {@link ShellRuntime} the frame renders with, from the enriched plan + the route. Picks
 * the active app (and highlights its tab), sets the account block from the signed-in user, and wires
 * navigation so the launcher and nav strip are live. Leaves `content` undefined so the frame derives
 * the app landing / tab view from the plan itself.
 */
/**
 * CAOS-1809 — the viewer, spelled the way a PLACED COMPONENT asks for it.
 *
 * A component input may declare `source: "userContext"` — the kernel documents it as "a fact about
 * the person reading it" — and until this existed nothing supplied one: the page renderer resolved
 * `urlParameter` and nothing else, so a placed `caos-topbar` drew with no name, and with no name its
 * own manifest says "the whole account control is hidden". The value was never missing. It is right
 * here, in the same `user` record {@link buildShellRuntime} builds below and `shell-frame.ts` hands
 * to that component's `user-name` attribute by hand.
 *
 * THIS FUNCTION IS THE TRANSLATION, AND THE ONLY ONE. The shell calls the display name `name`; a
 * shipped manifest calls the input `user_name`. `urlParameter` and `recordField` need no such map —
 * a route parameter and a field are already named what the input is named — so rather than let each
 * caller guess at it, the mapping lives once, next to the record it reads, and the resolver in
 * `page-surface.ts` stays a plain keyed lookup.
 *
 * THE KEYS ARE lower_snake AND THAT IS NOT A STYLE CHOICE. This function first shipped keyed
 * `userName`/`userSub`, read off `caos-ui/src/components/topbar.docs.ts`, and it could never have
 * worked: `caos-ui/scripts/gen-component-manifests.mjs` emits every manifest input as
 * `apiNameOf(input.name)`, so the manifest an org actually installs
 * (`caos-pkg-component-library/src/components/topbar.json`) names them `user_name` and `user_sub`.
 * The kernel agrees and is stricter than a convention — `InputName` is `BARE_IDENTIFIER_PATTERN`
 * over `[a-z][a-z0-9_]{0,59}`, so `userName` is not a legal input name at all and the deploy gate
 * would refuse it. A camelCase key here can never match a declared input, on any component.
 *
 * The docs file is the component's own documentation and names inputs by the JS property the piece
 * observes; the generated JSON is what reaches a workspace. Keying off the first was reading the
 * wrong end of a generator, and the suite agreed because its fixture's input name was hand-written
 * here rather than taken from the producer. `caos-1809-the-viewer-reaches-a-component.test.ts` now
 * reads the shipped manifest off the package shelf for exactly that reason.
 *
 * WHAT IT DOES NOT CARRY. `roles` is left out: which roles somebody holds is a permission question,
 * and the host already answers it through `heldPermissions` and the object permission planes. An
 * input is not a second, unchecked route to it. `sub` IS carried — `caos-topbar` names that input
 * `user_sub` and draws it as "a role, a company, an email" — and the shipped manifest asks for it:
 * CAOS-1811 gave that input `source: "userContext"`. This key was supplied one ticket ahead of the
 * manifest that wanted it, which is why the paragraph here used to read "supplying the key changes
 * nothing until that manifest asks for it". It does something now.
 *
 * ABSENT RATHER THAN BLANK, throughout. No session ⇒ `undefined` and every `userContext` input is
 * left off its element, which is what a surface above a sign-in screen is. A session whose name is
 * genuinely empty is a different fact and the resolver keeps it distinct. `sub` is a third case and
 * sits with the shell rather than with the resolver: a falsy `sub` omits the key, matching
 * `shell-frame.ts`'s own `if (runtime.user.sub)`, because a session whose sub line derives to ""
 * has no sub line rather than a blank one.
 */
export function viewerInputContext(
  user: ShellRuntime['user'],
): Readonly<Record<string, string>> | undefined {
  if (!user) return undefined;
  return {
    user_name: user.name,
    ...(user.sub ? { user_sub: user.sub } : {}),
  };
}

export function buildShellRuntime(plan: ShellFramePlan, input: ShellRuntimeInput): ShellRuntime {
  const { appKey, tabKey } = resolveActiveRoute(input.pathname, plan);
  const apps = plan.injections[NAVIGATION_ENTRIES] ?? [];
  const active = apps.find((a) => a.sourceKey === appKey);

  return {
    user: input.userName
      ? {
          name: input.userName,
          ...(input.userSub ? { sub: input.userSub } : {}),
          ...(input.userRoles && input.userRoles.length > 0 ? { roles: input.userRoles } : {}),
        }
      : undefined,
    ...(input.environmentLabel ? { env: input.environmentLabel } : {}),
    ...(input.impersonation ? { impersonation: input.impersonation } : {}),
    ...(input.impersonationOptions && input.impersonationOptions.length > 0
      ? { impersonationOptions: input.impersonationOptions }
      : {}),
    activeAppKey: appKey,
    activeAppLabel: active?.label,
    activeTabKey: tabKey,
    // CAOS-694/CAOS-342/CAOS-1659 — asked per route rather than resolved once. The gate is
    // `resolveSettingsHref` above; `settingsHrefFor` on `ShellRuntime` says why a value fixed at
    // assembly is wrong. `administers` is closed over rather than re-read because it is a fact
    // about the SESSION, settled when this runtime was built, while the ACTIVE APP is a fact
    // about the route — so exactly one of the gate's two halves varies, and it is the one the
    // route decides.
    settingsHrefFor: (route) => resolveSettingsHref(plan, input.administers, route),
    ...(input.editPageHrefFor ? { editPageHrefFor: input.editPageHrefFor } : {}),
    // CAOS-606 — passed through even when empty, because the frame has to tell "this person holds
    // one role, so there is nothing to switch to" from "nobody has asked yet": the first draws no
    // switcher, and so does the second, but only the first is a settled answer.
    ...(input.previewRoles && input.previewRoles.length > 0 ? { previewRoles: input.previewRoles } : {}),
    ...(input.activePreview ? { activePreview: input.activePreview } : {}),
    navigate: input.navigate,
  };
}

/**
 * CAOS-439 — does the application at this path say what it opens on?
 *
 * Exported and pure because it is the whole of a decision that would otherwise sit inside a route
 * handler nothing can call: whether the hardcoded `/ai` fallback in `main.ts` still applies. A
 * wrong answer is not a visual defect — it is a person opening an application and being shown
 * either somebody else's scoreboard or nothing at all — so it is measured directly rather than
 * inferred from a screen.
 *
 * IT LIVES HERE RATHER THAN IN `main.ts`, and that is not filing. `main.ts` mounts the workspace as
 * an import side effect, so a test that imported it threw before collecting — which vitest reports
 * as "no tests" rather than as a failure, the exact shape of a suite that silently subtracts itself.
 * A decision worth testing has to sit in a module that can be imported without starting the
 * application, and this one already owns apps, their tabs and their landing tabs.
 *
 * BOTH HALVES ARE REQUIRED, and the second is a MIRROR rather than a judgement of its own.
 * `landingTabKey` in `surfaces/shell-frame.ts` decides which tab an app actually opens on, and it
 * honours a declared `landingTab` under exactly this predicate — the key is set, AND some tab in
 * the app carries it. Asking the identical question here is what makes the yield safe: the
 * fallback stands down precisely when the frame will honour the declaration, and never on the
 * strength of a name the frame would ignore. `narrowPlanToPreview` above produces that shape on
 * purpose, dropping a landing declaration when the tab it names does not survive the preview, and
 * an authoring mistake produces it by accident; both are refused here for the frame's reason.
 *
 * WHAT THIS DOES NOT DECIDE, said rather than implied, because the obvious reading of the line
 * below is wrong. It does not ask whether the named tab RESOLVES to anything. The kernel checks
 * membership of the app's own `tabs` array and stops there (`api/metadata-routes.ts`), and
 * `metadata-source.ts` maps every declared key into `tabs` whether or not a tab component resolved
 * for it — so a key naming no deployed tab is present in `app.tabs` and counts as carried. That is
 * deliberate, not an oversight: the frame opens that tab, `content-source.ts` finds no object, no
 * page and no url behind it and returns undefined, and the app's own tab cards are drawn. Every app
 * with a dangling landing tab has behaved that way since CAOS-302, this branch included. Refusing
 * to yield on it would not repair the dangling tab — it would only hold the assistant on somebody
 * else's scoreboard instead, which is worse than the incumbent answer and is the thing CAOS-439
 * exists to stop.
 */
export function appDeclaresItsLanding(meta: { readonly apps: readonly AppMeta[] }, appPath: string): boolean {
  const app = meta.apps.find((a) => `/${a.key}` === appPath);
  if (!app || app.landingTab === undefined) return false;
  // Asked through the one function rather than restated: this is true exactly when the declaration
  // is the tab an app opens on, which is what the paragraph above means by a mirror.
  return landingTabKey(app) === app.landingTab;
}

/**
 * CAOS-302 / CAOS-1649 — THE tab an app opens on: its declared `landingTab` when that tab exists,
 * else its first tab. `undefined` when the app has no tabs.
 *
 * ## Why this is one exported function and not three agreeing copies
 *
 * Three places need this answer and each used to work it out for itself: the frame's chrome
 * (`populateStrip`, which highlights the tab), {@link appDeclaresItsLanding} (whose own comment
 * calls itself "a MIRROR rather than a judgement of its own" and explains at length why it must ask
 * the identical question), and — the one that was missing it — `parseContentRoute`, which resolves
 * what the CONTENT region draws.
 *
 * The cost of the third one not asking was the whole of CAOS-1649, and it was invisible because the
 * two halves disagreed QUIETLY: opening an app at its own address (`/feedback`, which is where the
 * Home screen's app card goes) highlighted the landing tab in the strip and drew something else
 * underneath it — a bare object roster of every field, built from describe, with none of the
 * authored list view's columns, presentation, search, sort or filters. Both halves looked right in
 * isolation. Nothing on screen said the address had resolved to a tab in one place and to no tab in
 * the other.
 *
 * It takes a structural type so that the frame's `ShellInjectionRef` and metadata's `AppMeta` can
 * both ask it, since the question is about the shape — a landing declaration and a set of tabs —
 * rather than about either type.
 */
export function landingTabKey(app: {
  readonly landingTab?: string;
  readonly tabs?: readonly { readonly key: string }[];
}): string | undefined {
  const tabs = app.tabs ?? [];
  // A declaration naming a tab the app does not carry is IGNORED rather than honoured, which is the
  // behaviour `narrowPlanToPreview` relies on: it drops a landing declaration whose tab did not
  // survive the preview, and the fallback to the first tab is what keeps that app openable.
  if (app.landingTab !== undefined && tabs.some((t) => t.key === app.landingTab)) return app.landingTab;
  return tabs[0]?.key;
}
